import React from 'react';
import { Target, Building2, Shield, BarChart3, MapPin, Train } from 'lucide-react';

const BusinessObjectives: React.FC = () => {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="bg-accent-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="w-8 h-8 text-accent-600" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-primary-900">
            Business Objectives
          </h2>
          <p className="text-gray-600">
            We target urban dwellers seeking modern, affordable housing and investors looking for high-yield real estate opportunities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Property Specifications */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <Building2 className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Property Specifications
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Offices B1(A) use class circa 230 to 2,800 square metres for conversion to residential units</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Convert into 5 to 50 residential units (C3 residential use)</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Mixed-use buildings acceptable</span>
              </li>
            </ul>
          </div>

          {/* Development Goals */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <BarChart3 className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Development Goals
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Target 100 units within 5 years</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Focus on one to two-bedroom starter homes</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>10% annual ROI for investors</span>
              </li>
            </ul>
          </div>

          {/* Quality & Innovation */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <Shield className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Quality & Innovation
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Quality, innovation and market positioning</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Foster sustainable community-orientated living</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Comprehensive risk management</span>
              </li>
            </ul>
          </div>

          {/* Property Types */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <Building2 className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Property Types
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Commercial-to-residential conversion</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Land with C3 residential planning permission</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Light industrial properties for C3 conversion</span>
              </li>
            </ul>
          </div>

          {/* Location Requirements */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <MapPin className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Location Requirements
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Non-flood zone areas in major towns and cities</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Focus on Home Counties</span>
              </li>
            </ul>
          </div>

          {/* Accessibility */}
          <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
            <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <Train className="w-6 h-6 text-primary-700" />
            </div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
              Accessibility
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Good access to public transport</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                <span>Close proximity to amenities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessObjectives;